import Product from './Product';
import {
  reRunScripts,
  reinitSwymActionButtons,
  reinitYotpoWidgets,
} from '../../helpers';

const selectors = {
  swatchProductUrl: '[data-swatch-product-url]',
  product: '[data-product]',
  syncSection: '[data-product-sync]',
};

/**
 * Variant from Liquid `data-swatch-pdp-variant-id` (PDP size + this color). Optional.
 *
 * @param {HTMLElement} el
 * @returns {string | undefined}
 */
function getSwatchPdpVariantId(el) {
  const id = el.dataset.swatchPdpVariantId;
  return id && id !== '' ? id : undefined;
}

/**
 * Appends &variant= when present (Section Rendering + product request).
 *
 * @param {string} pathWithQuery
 * @param {string} [variantId]
 * @returns {string}
 */
function withVariantParam(pathWithQuery, variantId) {
  if (!variantId) {
    return pathWithQuery;
  }
  const joiner = pathWithQuery.includes('?') ? '&' : '?';
  return `${pathWithQuery}${joiner}variant=${encodeURIComponent(variantId)}`;
}

/**
 * Async product switching: reads handle + optional variant from the clicked swatch (Liquid data attrs).
 *
 * @export
 * @class ProductAsyncSwitcher
 */
export default class ProductAsyncSwitcher {
  /**
   * @param {HTMLElement} el
   * @param {string} sectionId
   * @param {Object} [productConfig] - Parent Product config (e.g. isQuickShop, quickShopUrl)
   */
  constructor(el, sectionId, productConfig = {}) {
    this.el = el;
    this.sectionId = sectionId;
    this.productConfig = productConfig;

    const swatchProductUrls = el.querySelectorAll(selectors.swatchProductUrl);
    swatchProductUrls.forEach(link => {
      const handle = link.dataset.swatchProductUrl;
      if (handle) {
        link.addEventListener('click', e => {
          e.preventDefault();
          const target = e.currentTarget;
          if (!(target instanceof HTMLElement)) {
            return;
          }
          const variantId = getSwatchPdpVariantId(target);
          if (!this.productConfig.isQuickShop) {
            this._updateURL(handle, variantId);
          }
          this._fetchProductByHandle(handle, variantId);
          const optionVariantQuery = variantId
            ? `&variant=${encodeURIComponent(variantId)}`
            : '';
          const syncSections = document.querySelectorAll(selectors.syncSection);
          syncSections.forEach(section => {
            const syncSectionId = section.dataset.sectionId;
            if (syncSectionId) {
              this._fetchSectionByHandle(
                section,
                handle,
                syncSectionId,
                optionVariantQuery,
              );
            }
          });
        });
      }
    });
  }

  _fetchProductByHandle(handle, variantId) {
    const path = `${window.Shopify.routes.root}products/${handle}`;
    const baseUrl = withVariantParam(
      `${path}?section_id=${this.sectionId}`,
      variantId,
    );
    fetch(baseUrl)
      .then(response => response.text())
      .then(responseText => {
        const html = responseText;
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const newRoot = parsedHTML.querySelector(selectors.product);
        if (!newRoot) {
          return;
        }
        const variantInventory = newRoot.dataset.variantInventory;
        this.el.dataset.variantInventory = variantInventory;
        this.el.innerHTML = newRoot.innerHTML;
        reRunScripts(this.el);
        const form = this.el.querySelector('[data-product-form]');
        const nextConfig = {
          ...this.productConfig,
          klaviyoTrackViewedProduct: true,
        };
        if (this.productConfig.isQuickShop) {
          nextConfig.quickShopUrl =
            form?.dataset.productUrl || this.productConfig.quickShopUrl;
        }
        new Product(this.el, nextConfig);
        reinitSwymActionButtons(this.el);
        reinitYotpoWidgets();
      });
  }

  _fetchSectionByHandle(el, handle, sectionId, variantQuery = '') {
    fetch(
      `${window.Shopify.routes.root}products/${handle}?section_id=${sectionId}${variantQuery}`,
    )
      .then(response => response.text())
      .then(responseText => {
        const html = responseText;
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const newRoot = parsedHTML.querySelector(selectors.syncSection);
        el.innerHTML = newRoot.innerHTML;
        reRunScripts(el);
        reinitYotpoWidgets();
      });
  }

  _updateURL(handle, variantId) {
    const path = `${window.Shopify.routes.root}products/${handle}`;
    if (variantId) {
      const u = new URL(path, window.location.origin);
      u.searchParams.set('variant', variantId);
      history.replaceState({}, '', u.toString());
    } else {
      history.replaceState({}, '', path);
    }
  }
}
